import { useState, useEffect } from "react";
import axios from "axios";
import { Calendar, Send, CheckCircle2, ArrowLeft } from "lucide-react";
import { API } from "@/api";


function fmtSlot(iso) {
  try {
    return new Date(iso).toLocaleString("fr-CA", {
      weekday: "long", day: "numeric", month: "long",
      hour: "2-digit", minute: "2-digit",
    });
  } catch { return iso; }
}

/**
 * Iter100 — Page publique (no auth) /book pour réserver un RDV.
 * Calendly-like : liste des créneaux disponibles + formulaire simple.
 */
export default function PublicBookingPage() {
  const [slots, setSlots] = useState([]);
  const [loading, setLoading] = useState(true);
  const [selected, setSelected] = useState(null);
  const [form, setForm] = useState({ nom: "", email: "", phone: "", message: "", website: "" });
  const [submitting, setSubmitting] = useState(false);
  const [confirmation, setConfirmation] = useState(null);
  const [error, setError] = useState("");

  useEffect(() => {
    axios.get(`${API}/public/availability?days=14`)
      .then((r) => setSlots(r.data?.slots || []))
      .catch(() => setSlots([]))
      .finally(() => setLoading(false));
  }, []);

  const submit = async (e) => {
    e.preventDefault();
    setError("");
    if (!selected || !form.nom || !form.email) {
      setError("Nom, email et créneau requis");
      return;
    }
    setSubmitting(true);
    try {
      const r = await axios.post(`${API}/public/book`, {
        nom: form.nom,
        email: form.email,
        phone: form.phone || null,
        message: form.message || null,
        slot: selected,
        duration_min: 60,
        type: "online",
        website: form.website,
      });
      setConfirmation(r.data);
    } catch (e) {
      setError(e.response?.data?.detail || "Erreur lors de la réservation");
    } finally {
      setSubmitting(false);
    }
  };

  // Group slots by date
  const grouped = slots.reduce((acc, s) => {
    const day = s.slot.split("T")[0];
    (acc[day] = acc[day] || []).push(s);
    return acc;
  }, {});

  if (confirmation) {
    return (
      <div className="min-h-screen bg-gradient-to-br from-emerald-50 to-cyan-50 flex items-center justify-center p-4" data-testid="public-booking-confirmation">
        <div className="bg-white rounded-2xl shadow-xl max-w-md w-full p-8 text-center">
          <div className="w-16 h-16 rounded-full bg-emerald-100 flex items-center justify-center mx-auto mb-4">
            <CheckCircle2 className="w-8 h-8 text-emerald-600" />
          </div>
          <h1 className="text-2xl font-bold text-gray-900 mb-2">RDV confirmé !</h1>
          <p className="text-gray-600 mb-1">
            <strong>{fmtSlot(confirmation.start_time)}</strong>
          </p>
          {confirmation.email_sent && (
            <p className="text-xs text-emerald-600 mt-3">
              Une confirmation a été envoyée à {form.email}
            </p>
          )}
          <p className="text-xs text-gray-400 mt-4">
            Référence : {confirmation.appointment_id?.slice(0, 8)}
          </p>
        </div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-gradient-to-br from-cyan-50 to-blue-50/30 py-10 px-4" data-testid="public-booking-page">
      <div className="max-w-3xl mx-auto">
        <div className="text-center mb-8">
          <div className="w-16 h-16 rounded-2xl bg-cyan-100 flex items-center justify-center mx-auto mb-4">
            <Calendar className="w-8 h-8 text-cyan-700" />
          </div>
          <h1 className="text-3xl font-bold text-gray-900 mb-2">Réserver un rendez-vous</h1>
          <p className="text-gray-500">Choisissez un créneau qui vous convient</p>
        </div>

        <div className="bg-white rounded-2xl shadow-sm border border-gray-200 p-6">
          {!selected && (
            <>
              <h2 className="font-semibold text-gray-900 mb-4">Créneaux disponibles</h2>
              {loading && <div className="text-center py-8 text-gray-400">Chargement...</div>}
              {!loading && slots.length === 0 && (
                <div className="text-center py-8 text-gray-400" data-testid="public-no-slots">
                  Aucun créneau disponible pour les 14 prochains jours
                </div>
              )}
              <div className="space-y-4">
                {Object.entries(grouped).map(([day, dSlots]) => (
                  <div key={day} data-testid={`public-day-${day}`}>
                    <div className="text-xs uppercase text-gray-500 font-semibold mb-2">
                      {new Date(day + "T12:00:00").toLocaleDateString("fr-CA", { weekday: "long", day: "numeric", month: "long" })}
                    </div>
                    <div className="grid grid-cols-3 sm:grid-cols-4 gap-2">
                      {dSlots.map((s) => (
                        <button
                          key={s.slot}
                          onClick={() => setSelected(s.slot)}
                          className="px-3 py-2 rounded-lg border border-cyan-200 bg-cyan-50/40 hover:bg-cyan-100 hover:border-cyan-400 text-cyan-800 text-sm font-medium transition"
                          data-testid={`public-slot-${s.slot}`}
                        >
                          {new Date(s.slot).toLocaleTimeString("fr-CA", { hour: "2-digit", minute: "2-digit" })}
                        </button>
                      ))}
                    </div>
                  </div>
                ))}
              </div>
            </>
          )}

          {selected && (
            <form onSubmit={submit} className="space-y-4" data-testid="public-booking-form">
              <button
                type="button"
                onClick={() => setSelected(null)}
                className="text-sm text-gray-500 hover:text-gray-700 flex items-center gap-1"
                data-testid="public-back-btn"
              >
                <ArrowLeft className="w-4 h-4" /> Choisir un autre créneau
              </button>
              <div className="bg-cyan-50 border border-cyan-200 rounded-lg p-3 text-sm">
                <span className="text-cyan-600 font-medium">Créneau sélectionné : </span>
                <span className="text-cyan-900 font-semibold">{fmtSlot(selected)}</span>
              </div>
              {/* Pot de miel anti-bot : invisible, doit rester vide */}
              <input type="text" name="website" tabIndex={-1} autoComplete="off" aria-hidden="true" value={form.website}
                onChange={(e) => setForm({ ...form, website: e.target.value })} className="absolute -left-[9999px] top-auto w-px h-px overflow-hidden" data-testid="public-form-website" />
              <input
                type="text"
                placeholder="Nom complet *"
                value={form.nom}
                onChange={(e) => setForm({ ...form, nom: e.target.value })}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="public-form-nom"
              />
              <input
                type="email"
                placeholder="Email *"
                value={form.email}
                onChange={(e) => setForm({ ...form, email: e.target.value })}
                required
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="public-form-email"
              />
              <input
                type="tel"
                placeholder="Téléphone (optionnel)"
                value={form.phone}
                onChange={(e) => setForm({ ...form, phone: e.target.value })}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="public-form-phone"
              />
              <textarea
                placeholder="Message (optionnel)"
                value={form.message}
                onChange={(e) => setForm({ ...form, message: e.target.value })}
                rows={3}
                className="w-full px-4 py-2.5 border border-gray-200 rounded-lg focus:outline-none focus:ring-2 focus:ring-cyan-500"
                data-testid="public-form-message"
              />
              {error && (
                <p className="text-sm text-red-600" data-testid="public-form-error">{error}</p>
              )}
              <button
                type="submit"
                disabled={submitting}
                className="w-full px-4 py-3 bg-cyan-600 text-white rounded-lg hover:bg-cyan-700 disabled:opacity-50 transition font-medium flex items-center justify-center gap-2"
                data-testid="public-form-submit"
              >
                <Send className="w-4 h-4" />
                {submitting ? "Réservation..." : "Confirmer le RDV"}
              </button>
            </form>
          )}
        </div>
      </div>
    </div>
  );
}
